import React, { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Building2, KeyRound, LogIn, Mail, MessageSquare, Smartphone } from "lucide-react";
import {
  loginApi,
  loginErr,
  LoginRequestInfo,
  LoginStep,
  MfaMethod,
} from "../../api/account/loginApi";
import { getAssertion, isWebAuthnSupported, webauthnErr } from "../../utils/webauthn";
import Logo from "../../assets/chargingadda-logo.png";
import "./LoginPage.scss";

interface LoginFlowProps {
  authRequestId: string;
}

type Phase =
  | "loading"
  | "password"
  | "mfa"
  | "change_password"
  | "setup_totp"
  | "forgot"
  | "forgot_sent"
  | "redirecting"
  | "dead";

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.3 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
    <path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/>
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.7-3.8-13.6-9.3l-7.8 6C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);

const MicrosoftMark = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F35325"/>
    <rect x="12" y="1" width="10" height="10" fill="#81BC06"/>
    <rect x="1" y="12" width="10" height="10" fill="#05A6F0"/>
    <rect x="12" y="12" width="10" height="10" fill="#FFBA08"/>
  </svg>
);

const METHOD_META: Record<MfaMethod, { label: string; hint: string; icon: React.ReactNode }> = {
  totp: { label: "Authenticator app", hint: "Enter the 6-digit code from your app", icon: <Smartphone size={18} /> },
  u2f: { label: "Security key", hint: "Touch your key or use your device's unlock", icon: <KeyRound size={18} /> },
  otp_sms: { label: "SMS code", hint: "We text a code to your phone", icon: <MessageSquare size={18} /> },
  otp_email: { label: "Email code", hint: "We email you a code", icon: <Mail size={18} /> },
};

/**
 * The in-app sign-in. Zitadel (Login V2) sends the browser here with
 * ?authRequest=<id>; every step goes through authz-api /login/*, which holds
 * the Zitadel session. When the flow finishes we follow the callback URL and
 * the existing /callback code exchange takes over.
 */
const LoginFlow: React.FC<LoginFlowProps> = ({ authRequestId }) => {
  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<LoginRequestInfo | null>(null);
  const [step, setStep] = useState<LoginStep | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [webauthnOptions, setWebauthnOptions] = useState<any>(null);
  const [newPw, setNewPw] = useState({ a: "", b: "" });
  const codeRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------- boot
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await loginApi.request(authRequestId);
        if (!alive) return;
        const target = new URL(r.data.login_url, window.location.origin);
        if (target.hostname !== window.location.hostname) {
          // Run on the org's own subdomain (cookies, PKCE verifier, RP id all live there).
          window.location.replace(`${r.data.login_url}?authRequest=${encodeURIComponent(authRequestId)}`);
          return;
        }
        setInfo(r.data);
        if (r.data.login_hint) setLoginName(r.data.login_hint);

        // Back from a single sign-on provider? Zitadel appends id + token to our success URL.
        const q = new URLSearchParams(window.location.search);
        const sso = q.get("sso");
        const intentId = q.get("id");
        const intentToken = q.get("token");
        if (sso || intentId) {
          // Never leave the one-time token in the address bar / history.
          window.history.replaceState({}, "", `${window.location.pathname}?authRequest=${encodeURIComponent(authRequestId)}`);
        }
        if (sso === "ok" && intentId && intentToken) {
          setPhase("redirecting");
          try {
            const v = await loginApi.idpFinish(authRequestId, intentId, intentToken);
            if (!alive) return;
            applyStep(v.data);
          } catch (err) {
            if (!alive) return;
            const { message, restart } = loginErr(err, "Single sign-on failed");
            setError(message);
            setPhase(restart ? "dead" : "password");
          }
          return;
        }
        if (sso === "failed") {
          // Zitadel appends error + error_description to the failure URL.
          const desc = q.get("error_description") || q.get("error") || "";
          setError(
            `Single sign-on was cancelled or failed at your identity provider${desc ? ` (${desc})` : ""}. You can try again or use your password.`,
          );
        }
        setPhase("password");
      } catch (e) {
        if (!alive) return;
        const { message } = loginErr(e, "Could not start sign-in");
        setError(message);
        setPhase("dead");
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRequestId]);

  // ------------------------------------------------------------ stepping
  const applyStep = useCallback((s: LoginStep) => {
    setStep(s);
    setError("");
    setCode("");
    if (s.next === "done" && s.callback_url) {
      setPhase("redirecting");
      window.location.href = s.callback_url;
      return;
    }
    if (s.next === "mfa") {
      setPhase("mfa");
      // One method: go straight to it.
      if (s.mfa_methods.length === 1) setMethod(s.mfa_methods[0]);
      else setMethod(null);
      return;
    }
    if (s.next === "change_password") return setPhase("change_password");
    if (s.next === "setup_totp") return setPhase("setup_totp");
    setPhase("password");
  }, []);

  const fail = (e: any, fallback: string) => {
    const { message, restart } = loginErr(e, fallback);
    setError(message);
    if (restart) setPhase("dead");
  };

  const restart = () => {
    // Login.tsx (without ?authRequest) starts a fresh authorize round trip.
    window.location.href = "/login";
  };

  // ------------------------------------------------------------ sso
  const startSso = async (idpId: string) => {
    setBusy(true);
    setError("");
    try {
      const r = await loginApi.idpStart(authRequestId, idpId);
      window.location.href = r.data.auth_url;
    } catch (err) {
      fail(err, "Could not start single sign-on");
      setBusy(false);
    }
  };

  const idpIcon = (type: string) => {
    if (type.includes("GOOGLE")) return <GoogleMark />;
    if (type.includes("AZURE")) return <MicrosoftMark />;
    return <Building2 size={18} />;
  };

  // ------------------------------------------------------------ password
  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginName.trim() || !password) return;
    setBusy(true);
    setError("");
    try {
      const r = await loginApi.password(authRequestId, loginName.trim(), password);
      setPassword("");
      applyStep(r.data);
    } catch (err) {
      fail(err, "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  // ----------------------------------------------------------------- mfa
  const ticket = step?.ticket || "";

  const challenge = useCallback(
    async (m: MfaMethod) => {
      if (!ticket) return;
      setBusy(true);
      setError("");
      setNotice("");
      try {
        const r = await loginApi.mfaChallenge(ticket, m);
        setStep((prev) => (prev ? { ...prev, ticket: r.data.ticket } : r.data));
        if (m === "u2f") {
          setWebauthnOptions(r.data.webauthn);
          if (!isWebAuthnSupported()) {
            setError("This browser cannot use security keys. Pick another method.");
            return;
          }
          const cred = await getAssertion(r.data.webauthn);
          const v = await loginApi.mfaVerify(r.data.ticket || ticket, "u2f", { credential: cred });
          applyStep(v.data);
        } else if (r.data.code_sent) {
          setNotice(m === "otp_sms" ? "Code sent by SMS." : "Code sent by email.");
          setTimeout(() => codeRef.current?.focus(), 50);
        }
      } catch (err: any) {
        if (err?.name) setError(webauthnErr(err, "Security key failed"));
        else fail(err, "Could not start verification");
      } finally {
        setBusy(false);
      }
    },
    [ticket, applyStep],
  );

  // Kick off the challenge as soon as a method is chosen (sms/email send, u2f prompt).
  useEffect(() => {
    if (phase !== "mfa" || !method) return;
    if (method === "totp") {
      setTimeout(() => codeRef.current?.focus(), 50);
      return;
    }
    challenge(method);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, method]);

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!method || !ticket || code.trim().length < 4) return;
    setBusy(true);
    setError("");
    try {
      const r = await loginApi.mfaVerify(ticket, method, { code: code.trim() });
      applyStep(r.data);
    } catch (err) {
      fail(err, "That code was not accepted");
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------ change password
  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.a.length < 8) return setError("Use at least 8 characters.");
    if (newPw.a !== newPw.b) return setError("Passwords do not match.");
    if (!ticket) return;
    setBusy(true);
    setError("");
    try {
      const r = await loginApi.changePassword(ticket, newPw.a);
      setNewPw({ a: "", b: "" });
      applyStep(r.data);
    } catch (err) {
      fail(err, "Could not set the new password");
    } finally {
      setBusy(false);
    }
  };

  // ----------------------------------------------------------- setup totp
  useEffect(() => {
    if (phase !== "setup_totp" || !ticket || step?.totp_uri) return;
    (async () => {
      try {
        const r = await loginApi.setupTotp(ticket);
        setStep(r.data);
      } catch (err) {
        fail(err, "Could not start authenticator setup");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, ticket]);

  const submitTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your app.");
    setBusy(true);
    setError("");
    try {
      const r = await loginApi.setupTotpVerify(ticket, code);
      applyStep(r.data);
    } catch (err) {
      fail(err, "That code was not accepted");
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------- forgot
  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginName.trim()) return;
    setBusy(true);
    setError("");
    try {
      await loginApi.forgot(loginName.trim(), authRequestId);
      setPhase("forgot_sent");
    } catch (err) {
      fail(err, "Could not send the reset link");
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------- render
  const initial = (info?.org.name || "C").charAt(0).toUpperCase();

  const OrgHeader = () => (
    <div className="lgn-org">
      <div className="lgn-org__logo">
        {info?.org.logo ? <img src={info.org.logo} alt="" /> : initial}
      </div>
      <div>
        <div className="lgn-org__name">{info?.org.name || "ChargingAdda"}</div>
        <div className="lgn-org__sub">Charging Management Dashboard</div>
      </div>
    </div>
  );

  const UserChip = () =>
    step?.user ? (
      <div className="lgn-user">
        <div className="avatar">{(step.user.display_name || step.user.login_name || "U").charAt(0).toUpperCase()}</div>
        <div className="who">
          <strong>{step.user.display_name || step.user.login_name}</strong>
          <span>{step.user.login_name}</span>
        </div>
      </div>
    ) : null;

  const body = () => {
    switch (phase) {
      case "loading":
      case "redirecting":
        return (
          <>
            <div className="lgn-spinner" />
            <p className="lgn-sub" style={{ textAlign: "center" }}>
              {phase === "loading" ? "Preparing sign-in..." : <><LogIn size={14} style={{ verticalAlign: -2 }} /> Signing you in...</>}
            </p>
          </>
        );

      case "dead":
        return (
          <>
            <h1 className="lgn-title">Sign-in could not continue</h1>
            <p className="lgn-sub">{error || "This sign-in attempt is no longer valid."}</p>
            <button className="lgn-btn lgn-btn--primary" onClick={restart}>
              Start again
            </button>
          </>
        );

      case "password":
        if (info && !info.allow_password) {
          // SSO-only organisation
          return (
            <div className="lgn-form">
              <div>
                <h1 className="lgn-title">Sign in</h1>
                <p className="lgn-sub">{info.org.name} uses single sign-on. Continue with your company account.</p>
              </div>
              {error && <div className="lgn-error">{error}</div>}
              {info.idps.length === 0 ? (
                <div className="lgn-error">No sign-in method is available. Ask your administrator to enable one.</div>
              ) : (
                <div className="lgn-sso">
                  {info.idps.map((idp) => (
                    <button key={idp.id} type="button" className="lgn-btn lgn-btn--primary" disabled={busy} onClick={() => startSso(idp.id)}>
                      {idpIcon(idp.type)} Continue with {idp.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <form className="lgn-form" onSubmit={submitPassword}>
            <div>
              <h1 className="lgn-title">Sign in</h1>
              <p className="lgn-sub">Use your dashboard login for {info?.org.name}.</p>
            </div>
            <label>
              Email or username
              <input
                autoFocus={!loginName}
                autoComplete="username"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoFocus={!!loginName}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error && <div className="lgn-error">{error}</div>}
            <button className="lgn-btn lgn-btn--primary" type="submit" disabled={busy}>
              {busy ? "Checking..." : "Continue"}
            </button>
            {info?.password_reset !== false && (
              <div className="lgn-links">
                <button type="button" onClick={() => { setError(""); setPhase("forgot"); }}>
                  Forgot password?
                </button>
              </div>
            )}
            {(info?.idps?.length || 0) > 0 && (
              <>
                <div className="lgn-divider"><span>or</span></div>
                <div className="lgn-sso">
                  {info!.idps.map((idp) => (
                    <button key={idp.id} type="button" className="lgn-btn" disabled={busy} onClick={() => startSso(idp.id)}>
                      {idpIcon(idp.type)} Continue with {idp.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </form>
        );

      case "mfa": {
        const methods = step?.mfa_methods || [];
        if (!method) {
          return (
            <>
              <h1 className="lgn-title">Two-factor authentication</h1>
              <p className="lgn-sub">Choose how you want to verify it's you.</p>
              <UserChip />
              <div className="lgn-methods">
                {methods.map((m) => (
                  <button key={m} type="button" onClick={() => { setError(""); setMethod(m); }}>
                    <span className="ico">{METHOD_META[m].icon}</span>
                    <span>
                      {METHOD_META[m].label}
                      <small>{METHOD_META[m].hint}</small>
                    </span>
                  </button>
                ))}
              </div>
              {error && <div className="lgn-error" style={{ marginTop: 12 }}>{error}</div>}
            </>
          );
        }
        if (method === "u2f") {
          return (
            <>
              <h1 className="lgn-title">Use your security key</h1>
              <p className="lgn-sub">Insert and touch your key, or use your device's fingerprint / face unlock when prompted.</p>
              <UserChip />
              {busy && <div className="lgn-spinner" />}
              {error && <div className="lgn-error">{error}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                <button className="lgn-btn lgn-btn--primary" type="button" disabled={busy} onClick={() => challenge("u2f")}>
                  <KeyRound size={16} /> {webauthnOptions ? "Try again" : "Use security key"}
                </button>
              </div>
              {methods.length > 1 && (
                <div className="lgn-links">
                  <button type="button" onClick={() => { setError(""); setMethod(null); }}>Use a different method</button>
                </div>
              )}
            </>
          );
        }
        return (
          <form className="lgn-form" onSubmit={submitCode}>
            <div>
              <h1 className="lgn-title">{METHOD_META[method].label}</h1>
              <p className="lgn-sub">{METHOD_META[method].hint}.</p>
            </div>
            <UserChip />
            {notice && <div className="lgn-info">{notice}</div>}
            <input
              ref={codeRef}
              className="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
            />
            {error && <div className="lgn-error">{error}</div>}
            <button className="lgn-btn lgn-btn--primary" type="submit" disabled={busy || code.length < 4}>
              {busy ? "Verifying..." : "Verify"}
            </button>
            <div className="lgn-links">
              {method !== "totp" ? (
                <button type="button" disabled={busy} onClick={() => challenge(method)}>Resend code</button>
              ) : <span />}
              {methods.length > 1 && (
                <button type="button" onClick={() => { setError(""); setNotice(""); setMethod(null); }}>Use a different method</button>
              )}
            </div>
          </form>
        );
      }

      case "change_password":
        return (
          <form className="lgn-form" onSubmit={submitNewPassword}>
            <div>
              <h1 className="lgn-title">Set a new password</h1>
              <p className="lgn-sub">Your administrator set a temporary password. Choose your own to continue.</p>
            </div>
            <UserChip />
            <label>
              New password
              <input type="password" autoFocus autoComplete="new-password" minLength={8} value={newPw.a}
                     onChange={(e) => setNewPw({ ...newPw, a: e.target.value })} required />
            </label>
            <label>
              Confirm new password
              <input type="password" autoComplete="new-password" minLength={8} value={newPw.b}
                     onChange={(e) => setNewPw({ ...newPw, b: e.target.value })} required />
            </label>
            {error && <div className="lgn-error">{error}</div>}
            <button className="lgn-btn lgn-btn--primary" type="submit" disabled={busy}>
              {busy ? "Saving..." : "Save and continue"}
            </button>
          </form>
        );

      case "setup_totp":
        return (
          <form className="lgn-form" onSubmit={submitTotpSetup}>
            <div>
              <h1 className="lgn-title">Set up two-factor authentication</h1>
              <p className="lgn-sub">
                Your organisation requires it. Scan this code with Google Authenticator, Microsoft
                Authenticator, Authy or 1Password, then enter the 6-digit code it shows.
              </p>
            </div>
            <UserChip />
            {step?.totp_uri ? (
              <div className="lgn-qr">
                <QRCodeSVG value={step.totp_uri} size={168} level="M" />
                <code>{step.totp_secret}</code>
              </div>
            ) : (
              <div className="lgn-spinner" />
            )}
            <input
              className="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              disabled={!step?.totp_uri}
            />
            {error && <div className="lgn-error">{error}</div>}
            <button className="lgn-btn lgn-btn--primary" type="submit" disabled={busy || !step?.totp_uri}>
              {busy ? "Verifying..." : "Enable and continue"}
            </button>
          </form>
        );

      case "forgot":
        return (
          <form className="lgn-form" onSubmit={submitForgot}>
            <div>
              <h1 className="lgn-title">Reset your password</h1>
              <p className="lgn-sub">Enter your email or username and we'll send a reset link to the email on your account.</p>
            </div>
            <label>
              Email or username
              <input autoFocus autoComplete="username" value={loginName} onChange={(e) => setLoginName(e.target.value)} required />
            </label>
            {error && <div className="lgn-error">{error}</div>}
            <button className="lgn-btn lgn-btn--primary" type="submit" disabled={busy}>
              {busy ? "Sending..." : "Send reset link"}
            </button>
            <div className="lgn-links">
              <button type="button" onClick={() => { setError(""); setPhase("password"); }}>Back to sign in</button>
            </div>
          </form>
        );

      case "forgot_sent":
        return (
          <>
            <h1 className="lgn-title">Check your email</h1>
            <p className="lgn-sub">
              If an account exists for <strong>{loginName}</strong>, a reset link is on its way. It brings you back
              here to choose a new password.
            </p>
            <button className="lgn-btn" type="button" onClick={() => setPhase("password")}>Back to sign in</button>
          </>
        );
    }
  };

  return (
    <div className="lgn-page">
      <div className="lgn-card">
        <img className="lgn-brand" src={Logo} alt="ChargingAdda" />
        {phase !== "loading" && <OrgHeader />}
        {body()}
        <div className="lgn-foot">Secured by ChargingAdda</div>
      </div>
    </div>
  );
};

export default LoginFlow;
